import { computed, ref } from 'vue';

import api from '@/api';
import { mapToApi } from '@/mappers';
import { buildProductTypeRequestFromLoanApp, type BuildProductTypeRequestOptions } from '@/helpers/productTypeMatrix';

import type { ProductTypeResponse } from '@/api/backend/public/product-type';
import type { IncomingProperty, LoanApplication } from '@/types';

/** Live scheduled equity release inputs from the loan details form, not yet written back to `loanApp` */
export interface ScheduledEquityReleaseInput {
  amount: number;
  durationTotalMonths: number;
}

export function useCalcApi() {
  const isProcessing = ref(0);

  const getStampDuty = async (data: IncomingProperty): Promise<number> => {
    isProcessing.value++;
    try {
      const response = await api.calculator.formulas.stampDuty({
        postCode: data.address.postcode,
        ownershipType: data.propertyPurpose as never,
        purchasePrice: data.purchaseAmount as never,
        propertyType: data.propertyType,
      });
      return response.status === 200 ? Number(response.data.result) : 0;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  const getLoanAmount = async (loanApp: LoanApplication): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.loanAmount(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  const getMaxLoanAmount = async (loanApp: LoanApplication): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.maxLoanAmount(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  /**
   * ERHL total scheduled equity release, using the live release amount/term entered on the loan
   * details step rather than `loanApp.erhlDrawdowns` (which is only written back on submit).
   */
  const getTotalScheduledEquityRelease = async (
    loanApp: LoanApplication,
    scheduledEquityRelease: ScheduledEquityReleaseInput,
  ): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = {
        ...mapToApi.createApplication(loanApp),
        erhlDrawdowns: [
          {
            drawdownType: 'scheduled_equity_release',
            amount: scheduledEquityRelease.amount,
            durationTotalMonths: scheduledEquityRelease.durationTotalMonths,
          },
        ],
      };
      const response = await api.calculator.formulas.totalScheduledEquityRelease(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  const getSettlementFee = async (loanApp: LoanApplication): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.settlementFee(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  const getSetupFeeRate = async (loanApp: LoanApplication): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.setupFeeRate(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  /** Estimated amount to clear mortgages on supporting properties (ERHL) / outgoing properties (other products) */
  const getMortgagePayoutEstimate = async (loanApp: LoanApplication): Promise<number> => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.mortgagePayoutEstimate(payload);
      return response.data.result;
    } catch (err) {
      console.error(err);
      return 0;
    } finally {
      isProcessing.value--;
    }
  };

  const getEndDebt = async (loanApp: LoanApplication) => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.endDebt(payload);
      return response.result;
    } catch (err) {
      console.error(err);
    } finally {
      isProcessing.value--;
    }
  };

  const getNetSurplus = async (loanApp: LoanApplication) => {
    isProcessing.value++;
    try {
      const payload = mapToApi.createApplication(loanApp);
      const response = await api.calculator.formulas.netSurplus(payload);
      return response.result;
    } catch (err) {
      console.error(err);
    } finally {
      isProcessing.value--;
    }
  };

  const getProductType = async (
    loanApp: LoanApplication,
    requestOptions?: BuildProductTypeRequestOptions,
  ): Promise<ProductTypeResponse | undefined> => {
    isProcessing.value++;
    try {
      return await api.backend.public.getProductType(buildProductTypeRequestFromLoanApp(loanApp, requestOptions));
    } catch (err) {
      console.error('Failed to get product type:', err);
    } finally {
      isProcessing.value--;
    }
  };

  return {
    isLoading: computed(() => isProcessing.value > 0),
    getStampDuty,
    getLoanAmount,
    getMaxLoanAmount,
    getTotalScheduledEquityRelease,
    getSettlementFee,
    getSetupFeeRate,
    getMortgagePayoutEstimate,
    getEndDebt,
    getNetSurplus,
    getProductType,
  };
}
